import apiClient from "./apiClient";
import { getIntervalName } from "../utils/pricing";
import type { RegistrationSourcePayload } from "../utils/registrationSourceParams";

/** @deprecated Use CheckEligibilityRequest instead */
export interface CheckAvailabilityRequest {
  subdomain?: string;
  customDomain?: string;
  email?: string;
  nip?: string;
}

/**
 * Request DTO for 2-step registration eligibility check.
 * @see AC-1, AC-2
 */
export interface CheckEligibilityRequest {
  email: string;
  nip: string;
  phone: string;
}

/**
 * Response from POST /register/check-eligibility.
 * Contains email/NIP/phone availability and GUS company data.
 * @see AC-3, AC-4, AC-5
 */
export interface CheckEligibilityResponse {
  emailAvailable: boolean;
  nipAvailable: boolean;
  phoneAvailable: boolean;
  gusData: {
    companyName: string;
    street: string;
    city: string;
    postalCode: string;
    gusLookupSucceeded: boolean;
  } | null;
  /**
   * Per-user trial eligibility, computed by the backend via the same domain
   * service the real registration uses. Present only when registration can
   * proceed (email + NIP available). Absent → treat as eligible (backward
   * compatible with older backends). `eligible: false` → no free trial, the
   * account requires immediate payment.
   */
  trial?: {
    eligible: boolean;
  };
}

/**
 * Information about existing account if email/NIP already registered.
 * Used for duplicate prevention UX - shows login link when duplicate detected.
 *
 * @see docs/specifications/2026-02-18-prevent-duplicate-accounts.md
 */
export interface ExistingAccountInfo {
  hasAccount: boolean;
  message: string;
  loginUrl: string;
}

/** @deprecated Use CheckEligibilityResponse instead */
export interface CheckAvailabilityResponse {
  subdomain: {
    available: boolean;
    suggestion: string | null;
  };
  customDomain?: {
    available: boolean;
    message: string;
  };
  trial?: {
    eligible: boolean;
    trialDays: number;
    reason: string;
  };
  /** Whether email is available for registration */
  emailAvailable?: boolean;
  /** Whether NIP is available for registration */
  nipAvailable?: boolean;
  /** Information about existing account if email/NIP already registered */
  existingAccountInfo?: ExistingAccountInfo;
}

export interface SubscriptionPlan {
  /**
   * Legacy `subscription_plans` fields. The AC-9 3-package `/register/plans`
   * response never sends any of these — it returns exactly `{grantsScheme,
   * productId, displayName, marketingDescription, prices}`. Kept optional
   * (not removed) so legacy-shape callers/fixtures keep type-checking.
   */
  id?: string;
  name?: string;
  description?: string;
  stripeProductId?: string;
  includedModules?: string[];
  prices: SubscriptionPrice[];
  status?: string;
  /**
   * AC-9: registration-scope discriminator for the 3-package catalog (kfs/bur/all).
   * Absent on the legacy single-plan shape — Step4 falls back to treating that as 'all'.
   */
  grantsScheme?: "kfs" | "bur" | "all";
  /**
   * AC-9/AC-1: package name from BillingCatalogEntry.displayName, e.g.
   * "Standard KFS+BUR". Can be `null` (not just absent) when the catalog
   * lookup for `productId` finds nothing (e.g. seed not yet applied on this
   * environment) — Step4 must never render/announce that as "undefined".
   */
  displayName?: string | null;
  /** AC-9: 1-line "what's included" copy from BillingCatalogEntry.marketingDescription. */
  marketingDescription?: string | null;
  /** BillingProduct UUID — debugging only, never submitted by the frontend (AC-9). */
  productId?: string;
}

/**
 * Promotion data returned from the API when a promotion is active
 *
 * @deprecated-shape (semantyka "rabat") — od 2026-05 używane do komunikacji
 * NADCHODZĄCEJ PODWYŻKI ceny (grandfathering), nie obniżki.
 * originalAmount = przyszła wyższa cena, promotionalAmount = aktualna niższa.
 * Wygasza się 2026-07-01. TODO: po wygaśnięciu rozważyć refactor do
 * { kind: 'discount' | 'price_increase_grandfather' } lub osobne
 * `priceComparison` — patrz ADR-stub w docs/architecture/adr/.
 */
export interface PricePromotion {
  isActive: boolean;
  originalAmount: number; // Original price in cents (NET)
  promotionalAmount: number; // Discounted price in cents (NET)
  discountPercent?: number; // e.g., 10 for 10% — optional, not returned by backend for grandfathering scenario
  endDate?: string; // ISO date string — optional, not returned by backend
  label: string; // e.g., "Promocja -10% do 31 stycznia"
  /**
   * Lowest price in the last 30 days in cents (NET) — Omnibus directive requirement
   * @expires 2026-07-01
   */
  lowestPrice30Days?: number;
}

export interface SubscriptionPrice {
  /** Absent on the AC-9 3-package catalog shape — `stripePriceId` is unique enough within one plan's `prices[]`. */
  id?: string;
  stripePriceId: string;
  amount: number;
  currency: string;
  interval: "month" | "year";
  intervalCount: number;
  billingInterval: string; // "monthly" | "quarterly" | "quarterly_3" | "yearly"
  /** Human-readable label from backend, e.g. "miesięcznie", "kwartalnie", "rocznie" */
  billingIntervalLabel?: string;
  isActive: boolean;
  /** Promotion data if a promotion is active for this price, undefined otherwise */
  promotion?: PricePromotion;
}

export interface RegisterTenantRequest {
  tenantName: string;
  subdomain?: string;
  customDomain?: string;
  nip?: string;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  password: string;
  stripePriceId: string;
  /**
   * AC-9/AC-4: registration scope. `/register/create` (Stripe) accepts only
   * 'all' or absent (AC-12) — narrow schemes are Stripe-price-less by design,
   * so the UI never offers a Stripe price option when scope != 'all'.
   */
  grantsScheme?: "kfs" | "bur" | "all";
  // PA-5 company data fields
  companyName?: string;
  street?: string;
  city?: string;
  postalCode?: string;
  billingEmail?: string;
  gusLookupSucceeded?: boolean;
  // Tracking fields
  gaClientId?: string; // GA4 client_id for server-side conversion tracking
  fbc?: string; // Facebook Click ID for Meta Conversions API
  fbp?: string; // Facebook Browser ID for Meta Conversions API
  marketingConsent?: boolean; // GDPR marketing consent from cookie banner
  // RODO / consumer-rights: mandatory withdrawal-right waiver (art. 38 pkt 13
  // UPK) — immediate service access ⇒ loss of the 14-day distance-contract
  // withdrawal right. Sent as literal true; backend stamps the SoT version.
  withdrawalWaiverAccepted?: boolean;
  /**
   * 5b: single-use trial invite token (admin link `?invite=`). Only honoured
   * by `/register/create-direct`: trial without automatic proforma.
   */
  inviteToken?: string;
  /** Registration source (UTM etc.), `RegistrationSourceDto` — absent when unknown. */
  source?: RegistrationSourcePayload;
}

export interface RegisterTenantResponse {
  checkoutUrl: string;
}

export interface DirectActivationResponse {
  tenantId: string;
  subdomain: string;
  email: string;
  periodEnd: string;
  amountGross: number;
  currency: string;
  billingPeriod: string;
  eventId: string;
}

/**
 * Response from POST /register/trial-invite/check (5b, review MEDIUM-8).
 * `valid: false` is the same for unknown, used, expired and other-brand
 * tokens; the e-mail an invite is bound to is never returned.
 */
export type TrialInviteCheckResponse =
  | { valid: false }
  | { valid: true; trialDays: number; emailBound: boolean };

export interface SessionInfo {
  hasTrial: boolean;
  trialEndDate: string | null;
  trialDays: number | null;
  supportEmail: string | null;
  eventId: string | null; // Meta Pixel deduplication event_id (uuidv5 of subscriptionId)
}

/**
 * Registration Service
 *
 * Provides API client methods for tenant registration flow.
 * Handles subdomain validation, plan fetching, and Stripe Checkout session creation.
 */
export const registrationService = {
  /**
   * Check subdomain and custom domain availability
   */
  async checkAvailability(
    data: CheckAvailabilityRequest
  ): Promise<CheckAvailabilityResponse> {
    const response = await apiClient.post<CheckAvailabilityResponse>(
      "/register/check-availability",
      data
    );
    return response.data;
  },

  /**
   * Check registration eligibility: email + NIP availability + GUS lookup.
   * Step 1 of the 2-step registration flow.
   * @see AC-2, AC-3, AC-4, AC-5
   */
  async checkEligibility(
    data: CheckEligibilityRequest
  ): Promise<CheckEligibilityResponse> {
    const response = await apiClient.post<CheckEligibilityResponse>(
      "/register/check-eligibility",
      data
    );
    return response.data;
  },

  /**
   * 5b: can this trial invite be used on this brand right now? Does not
   * consume the token. Rate-limited per IP (429) — callers treat any error as
   * "unknown", not as an invalid invite.
   */
  async checkTrialInvite(
    inviteToken: string
  ): Promise<TrialInviteCheckResponse> {
    const response = await apiClient.post<TrialInviteCheckResponse>(
      "/register/trial-invite/check",
      { inviteToken }
    );
    return response.data;
  },

  /**
   * Get all available subscription plans with pricing
   */
  async getPlans(): Promise<SubscriptionPlan[]> {
    const response = await apiClient.get<SubscriptionPlan[]>(
      "/register/plans"
    );
    return response.data;
  },

  /**
   * Create new tenant and get Stripe Checkout URL
   */
  async registerTenant(
    data: RegisterTenantRequest
  ): Promise<RegisterTenantResponse> {
    const response = await apiClient.post<RegisterTenantResponse>(
      "/register/create",
      data
    );
    return response.data;
  },

  /**
   * Get trial information from Stripe checkout session
   * Used to conditionally display trial message on registration success page
   */
  async getSessionInfo(sessionId: string): Promise<SessionInfo> {
    const response = await apiClient.get<SessionInfo>(
      "/register/session-info",
      {
        params: { session_id: sessionId },
      }
    );
    return response.data;
  },

  /**
   * Format price for display (PLN currency)
   */
  formatPrice(amount: number, currency: string): string {
    return new Intl.NumberFormat("pl-PL", {
      style: "currency",
      currency: currency.toUpperCase(),
      currencyDisplay: "code",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount / 100);
  },

  /**
   * Get interval display name in Polish
   */
  getIntervalName(interval: string, intervalCount: number): string {
    return getIntervalName(interval, intervalCount);
  },

  /**
   * Create new tenant with direct activation (no Stripe checkout)
   * Used for bank transfer payments with proforma invoice
   */
  async registerTenantDirect(
    data: RegisterTenantRequest
  ): Promise<DirectActivationResponse> {
    const response = await apiClient.post<DirectActivationResponse>(
      "/register/create-direct",
      {
        ...data,
        billingPeriod: getBillingPeriodFromPriceId(data.stripePriceId),
      }
    );
    return response.data;
  },

  /**
   * Check if price ID is for direct activation (bank transfer) vs Stripe
   * Direct prices start with 'direct_' prefix
   * Stripe prices start with 'price_1' (real Stripe price IDs)
   */
  isDirectPrice(stripePriceId: string): boolean {
    return stripePriceId.startsWith("direct_");
  },
};

/**
 * Extract billing period from direct price ID
 * Format: direct_<uuid>_<interval> (e.g., direct_21c4531f_monthly)
 */
function getBillingPeriodFromPriceId(priceId: string): string {
  if (priceId.endsWith("_monthly")) return "monthly";
  if (priceId.endsWith("_quarterly")) return "quarterly";
  if (priceId.endsWith("_yearly")) return "yearly";
  return "monthly";
}
